import { useCallback, useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, ChartLineUp, MapPin, Storefront, PaperPlaneTilt } from "@phosphor-icons/react";
import { btnPrimary } from "@/components/market/shared";
import { RewardDashboard } from "@/components/market/RewardDashboard";
import { LocationExplorer } from "@/components/market/LocationExplorer";
import { MyBusinessPanel } from "@/components/market/MyBusinessPanel";
import { MyRequests } from "@/components/market/MyRequests";
import { RegisterBusinessDialog } from "@/components/market/RegisterBusinessDialog";
import { ShareCelebration } from "@/components/market/ShareCelebration";

const TABS = [
  ["dashboard", "Dashboard", ChartLineUp],
  ["locations", "Location", MapPin],
  ["business", "My Business", Storefront],
  ["requests", "My Requests", PaperPlaneTilt],
];

export default function Market() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "dashboard";
  const [data, setData] = useState(null);
  const [regOpen, setRegOpen] = useState(false);

  const { refresh } = useAuth();
  const load = useCallback(() => {
    api.get("/market/dashboard").then(({ data: d }) => setData(d)).catch(() => {});
    refresh();
  }, [refresh]);
  useEffect(() => { load(); }, [load]);

  const canRegister = !!data && (!data.my_business || (data.my_business.status === "rejected" && !data.my_business.deleted));

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 via-sky-50 to-white text-sky-950" data-testid="market-page">
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-10">
        <Link to="/inbox" className="inline-flex items-center gap-1 text-sm text-sky-700 hover:text-sky-900 mb-4" data-testid="market-back">
          <ArrowLeft size={16} /> Inbox
        </Link>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-sky-500">Tainapp</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight">Market</h1>
            <p className="text-sky-700 mt-1 text-sm md:text-base">Spend, earn reward shares, and trade with businesses near you.</p>
          </div>
          {canRegister && (
            <button data-testid="register-business-btn" className={btnPrimary} onClick={() => setRegOpen(true)}>
              <Storefront size={18} weight="bold" /> REGISTER AS A BUSINESS
            </button>
          )}
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 mb-6" data-testid="market-tabs">
          {TABS.map(([key, label, Icon]) => (
            <button key={key} data-testid={`market-tab-${key}`} onClick={() => setParams({ tab: key })}
              className={`shrink-0 inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-bold transition-colors ${tab === key ? "bg-sky-950 text-white" : "bg-white/80 text-sky-800 hover:bg-white"}`}>
              <Icon size={16} weight="bold" /> {label}
            </button>
          ))}
        </div>

        {!data ? (
          <div className="h-64 rounded-3xl bg-white/60 animate-pulse" />
        ) : (
          <>
            {tab === "dashboard" && <RewardDashboard data={data} reload={load} />}
            {tab === "locations" && <LocationExplorer categories={data.categories} initialLocation={params.get("loc")} />}
            {tab === "business" && <MyBusinessPanel biz={data.my_business} categories={data.categories} spendFee={data.spend_entry_fee_credits || 0} promo={data.promo} onRegister={() => setRegOpen(true)} reload={load} />}
            {tab === "requests" && <MyRequests />}
          </>
        )}
      </div>
      {data?.celebrate_shares > 0 && <ShareCelebration shares={data.celebrate_shares} onDone={load} />}
      <RegisterBusinessDialog open={regOpen} onClose={() => setRegOpen(false)} fee={data?.registration_fee_credits || 0}
        categories={data?.categories || []} credits={data?.credits || 0} onDone={() => { load(); setParams({ tab: "business" }); }} />
    </div>
  );
}
